import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Upload, Video, AlertCircle, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { useNavigate as useNavLink } from 'react-router-dom';
import { getCategories, getModules, addModule } from '@/lib/storage';
import { addCategory } from '@/lib/supabase';
import type { Category, Module } from '@/types';
import { uploadToVimeo, getBackendUrl } from '@/lib/vimeo';
import { getCurrentUser } from '@/lib/auth';
import { api } from '@/lib/api';
import FichaDaAula, { type Ficha } from '@/components/admin/FichaDaAula';

export default function VimeoUpload() {
  const navigate = useNavigate();
  const nav = useNavLink();
  const location = useLocation();
  const { toast } = useToast();
  
  const [hasServerToken, setHasServerToken] = useState<boolean | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ficha, setFicha] = useState<Ficha>({ mentorName: '', mentorBio: '', mentorPhoto: null });
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [moduleId, setModuleId] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [modules, setModules] = useState<Array<Pick<Module, 'id' | 'title' | 'parentId' | 'order'>>>([]);
  const [catSearch, setCatSearch] = useState('');
  const [moduleSearch, setModuleSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedVideoId, setUploadedVideoId] = useState<string | null>(null);
  const [uploadedEmbedUrl, setUploadedEmbedUrl] = useState<string | null>(null);

  // Verificar se o backend possui token permanente configurado
  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const data = await api.getVimeoTokenStatus();
        if (!canceled) setHasServerToken(!!data?.hasToken);
      } catch {
        if (!canceled) setHasServerToken(false);
      }
    })();
    return () => { canceled = true; };
  }, []);

  // Carregar categorias
  useEffect(() => {
    (async () => {
      const cats = await getCategories();
      setCategories(cats);
    })();
  }, []);

  // Carregar módulos quando as categorias mudarem (união)
  useEffect(() => {
    (async () => {
      if (!categoryIds || categoryIds.length === 0) {
        setModules([]);
        setModuleId('');
        return;
      }
      const results: Array<Pick<Module, 'id' | 'title' | 'parentId' | 'order'>> = [];
      for (const cid of categoryIds) {
        const list = await getModules(cid);
        for (const m of list) {
          results.push({ id: m.id, title: m.title, parentId: m.parentId ?? null, order: m.order });
        }
      }
      setModules(results);
      setModuleId('');
    })();
  }, [categoryIds]);

  // Handle OAuth callback (mantido apenas para compatibilidade; ignora com token permanente)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code && state) {
      // não faz nada, usamos token do servidor
      navigate('/admin/vimeo-upload', { replace: true });
    }
  }, [location, navigate]);

  // Mantido apenas para compatibilidade de rota; não usado com token permanente
  const exchangeCodeForToken = async (_code: string, _state: string) => {
    const savedState = localStorage.getItem('vimeo_oauth_state');
    
    if (_state !== savedState) {
      toast({
        title: 'Erro de segurança',
        description: 'Estado OAuth inválido.',
        variant: 'destructive'
      });
      return;
    }
    
    localStorage.removeItem('vimeo_oauth_state');
    // fluxo OAuth desativado (usamos token do servidor)

    try {
      const response = await api.vimeoAuth({
        action: 'exchangeToken',
        code: _code,
        state: _state,
      });

      if (!response.ok) {
        throw new Error('Token exchange failed');
      }

      await response.json();
      
      // Clear URL params
      navigate('/admin/vimeo-upload', { replace: true });
      
      toast({
        title: 'Sucesso!',
        description: 'Autenticação com Vimeo realizada com sucesso.',
      });
    } catch (error) {
      console.error('Token exchange error:', error);
      toast({
        title: 'Erro',
        description: 'Falha na autenticação com Vimeo.',
        variant: 'destructive'
      });
    } finally { /* noop */ }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type.startsWith('video/')) {
        setFile(selectedFile);
      } else {
        toast({
          title: 'Arquivo inválido',
          description: 'Por favor, selecione um arquivo de vídeo.',
          variant: 'destructive'
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !title || categoryIds.length === 0) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, selecione ao menos uma categoria e informe o título/arquivo.',
        variant: 'destructive'
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    let etapa = 'criar o envio no Vimeo';

    try {
      // Step 1: Create video on Vimeo and get upload URL
      // sem accessToken: o servidor usa o token permanente dele
      const { uploadLink, videoId, embedUrl } = await api.vimeoCreateUpload(
        {
          title,
          description,
          privacy: {
            view: 'unlisted',
            embed: 'public',
            download: false,
            add: false,
            comments: 'nobody',
          },
        },
        file.size,
      );
      
      // Step 2: Upload the actual video file
      etapa = 'enviar o arquivo ao Vimeo';
      await uploadToVimeo(file, uploadLink, (percentage) => {
        setUploadProgress(percentage);
      });

      // Step 3: Fetch best thumbnail and duration from Vimeo
      let thumbnailUrl = '';
      let durationSec = 0;
      try {
        const { thumbnail, duration } = await api.getVimeoDetails(String(videoId));
        if (thumbnail) thumbnailUrl = thumbnail;
        if (typeof duration === 'number') durationSec = duration;
      } catch (e) {
        console.warn('Falha ao obter thumbnail/duração do Vimeo');
      }

      // Step 4: Save video in Railway
      etapa = 'salvar a aula no portal';
      const currentUser = getCurrentUser();
      const mainId = categoryIds[0];
      const allCats = Array.from(new Set(categoryIds));
      await api.addVideo({
        title,
        description,
        video_url: `https://vimeo.com/${videoId}`,
        thumbnail: thumbnailUrl,
        category_id: mainId,
        category_ids: allCats,
        module_id: moduleId || undefined,
        duration: durationSec || 0,
        uploaded_by: currentUser?.name || 'admin',
        vimeo_id: videoId,
        vimeo_embed_url: embedUrl,
        mentor_name: ficha.mentorName || null,
        mentor_bio: ficha.mentorBio || null,
        mentor_photo: ficha.mentorPhoto || null,
      });
      
      setUploadedVideoId(videoId);
      setUploadedEmbedUrl(embedUrl);
      
      toast({
        title: 'Upload concluído!',
        description: 'Vídeo enviado com sucesso para o Vimeo.',
      });

      // Reset form
      setFile(null);
      setTitle('');
      setDescription('');
      setFicha({ mentorName: '', mentorBio: '', mentorPhoto: null });
      setCategoryIds([]);
      setModuleId('');
      setModules([]);
      
    } catch (error) {
      console.error('Upload error:', error);
      const rawMessage = error instanceof Error ? error.message : String(error);
      let detail = rawMessage;
      try { detail = JSON.parse(rawMessage)?.error || rawMessage; } catch { /* resposta não JSON */ }
      toast({
        title: 'Erro no upload',
        description: `Falha ao ${etapa}: ${detail}`,
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogout = () => { /* usando token do servidor, sem logout */ };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload de Vídeo para Vimeo</CardTitle>
          <CardDescription>
            Faça upload seguro de vídeos diretamente para o Vimeo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Server Token Status */}
          {hasServerToken === false && (
            <div className="space-y-2 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900">
              <p className="font-medium">O envio ao Vimeo ainda não está habilitado neste servidor.</p>
              <p>
                Falta a variável <code className="rounded bg-yellow-100 px-1">VIMEO_ACCESS_TOKEN</code>{' '}
                no Railway, no serviço do portal. Ela é o <em>access token</em> gerado em{' '}
                <a
                  href="https://developer.vimeo.com/apps"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  developer.vimeo.com/apps
                </a>{' '}
                com as permissões <strong>Upload</strong>, <strong>Edit</strong>,{' '}
                <strong>Delete</strong> e <strong>Private</strong> — não é o Client ID nem o
                Client Secret, que servem para outra coisa.
              </p>
              <p className="text-yellow-800">
                Depois de adicionar a variável, o Railway reinicia o serviço sozinho; recarregue
                esta página.
              </p>
            </div>
          )}

          {/* Upload Form */}
          {hasServerToken && !uploadedVideoId && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="video-file">Arquivo de Vídeo *</Label>
                <Input
                  id="video-file"
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  className="mt-1"
                />
                {file && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Selecionado: {file.name} ({Math.round(file.size / 1024 / 1024)}MB)
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Digite o título do vídeo"
                  disabled={isUploading}
                  className="mt-1"
                />
              </div>

              <FichaDaAula
                idPrefixo="vimeo"
                valor={{ ...ficha, description }}
                aoMudar={({ description: d, ...resto }) => {
                  if (d !== undefined) setDescription(d || '');
                  if (Object.keys(resto).length) setFicha((atual) => ({ ...atual, ...resto }));
                }}
              />

              {/* Removido select de categoria principal; multisseleção abaixo substitui */}

              <div>
                <div className="space-y-2 mt-2">
                    <Label>Categorias (múltiplas)</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="w-full min-h-10 border rounded px-2 py-2 text-left flex items-center flex-wrap gap-1">
                          {(() => {
                            const selected = categories.filter(c => categoryIds.includes(c.id));
                            return selected.length > 0 ? (
                              selected.map(s => (
                                <span key={s.id} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded">
                                  {s.name}
                                  {
                                    <button
                                      type="button"
                                      className="opacity-70 hover:opacity-100"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCategoryIds(prev => prev.filter(id => id !== s.id));
                                      }}
                                    >×</button>
                                  }
                                </span>
                              ))
                            ) : (
                              <span className="text-muted-foreground text-sm">Selecionar categorias...</span>
                            );
                          })()}
                          <span className="ml-auto text-muted-foreground text-sm">▼</span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-80">
                        <div className="px-2 py-2">
                          <Input placeholder="Buscar categoria..." value={catSearch} onChange={(e) => setCatSearch(e.target.value)} />
                        </div>
                        <div className="max-h-72 overflow-auto">
                          {categories
                            .filter((c) => (catSearch ? (c.name || '').toLowerCase().includes(catSearch.toLowerCase()) : true))
                            .map((category) => {
                              const checked = categoryIds.includes(category.id);
                              return (
                                <DropdownMenuItem
                                  key={category.id}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                    setCategoryIds(prev => checked ? prev.filter(id => id !== category.id) : Array.from(new Set([...prev, category.id])));
                                  }}
                                >
                                  <input type="checkbox" readOnly checked={checked} className="mr-2" />
                                  {category.name}
                                </DropdownMenuItem>
                              );
                            })}
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                </div>
              </div>

              <div>
                <Label>Módulo/Submódulo (opcional)</Label>
                <Input placeholder="Buscar módulo..." value={moduleSearch} onChange={(e) => setModuleSearch(e.target.value)} />
                <Select value={moduleId} onValueChange={setModuleId} disabled={categoryIds.length === 0 || modules.length === 0}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={categoryIds.length > 0 ? (modules.length ? 'Selecione o módulo' : 'Nenhum módulo nas categorias') : 'Escolha uma categoria primeiro'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const all = modules.filter(m => (moduleSearch ? (m.title || '').toLowerCase().includes(moduleSearch.toLowerCase()) : true));
                      const roots = all.filter((m) => !m.parentId).sort((a, b) => (Number(a.order || 0) - Number(b.order || 0)) || String(a.title).localeCompare(String(b.title)));
                      
                      // Função recursiva para renderizar módulos
                      const renderModuleOption = (module: Pick<Module, 'id' | 'title' | 'parentId'>, level: number): JSX.Element[] => {
                        const children = all
                          .filter(m => m.parentId === module.id)
                          .sort((a, b) => (Number(a.order || 0) - Number(b.order || 0)) || String(a.title).localeCompare(String(b.title)));
                        
                        const indent = '↳ '.repeat(level);
                        const padding = level * 1;
                        
                        const result: JSX.Element[] = [
                          <SelectItem key={module.id} value={module.id}>
                            <span style={{ paddingLeft: `${padding * 0.5}rem` }}>{indent}{module.title}</span>
                          </SelectItem>
                        ];

                        children.forEach(child => {
                          result.push(...renderModuleOption(child, level + 1));
                        });

                        return result;
                      };

                      return (
                        <div className="max-h-64 overflow-auto">
                          {roots.map((root) => (
                            <div key={root.id}>
                              <div className="px-2 py-1 text-xs text-muted-foreground uppercase">{root.title}</div>
                              {renderModuleOption(root, 0)}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>

              {/* Upload Progress */}
              {isUploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Enviando vídeo...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              {/* Upload Button */}
                <Button 
                onClick={handleUpload}
                disabled={isUploading || !file || !title || categoryIds.length === 0}
                className="w-full"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando... ({uploadProgress}%)
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Enviar para Vimeo
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Success Preview */}
          {uploadedVideoId && uploadedEmbedUrl && (
            <div className="space-y-4">
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Vídeo enviado com sucesso! ID do Vimeo: {uploadedVideoId}
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label>Pré-visualização do Vídeo</Label>
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <iframe
                    src={uploadedEmbedUrl}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="Vimeo video preview"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setUploadedVideoId(null);
                    setUploadedEmbedUrl(null);
                  }}
                  className="flex-1"
                >
                  Enviar Outro Vídeo
                </Button>
                <Button 
                  onClick={() => navigate('/admin')}
                  className="flex-1"
                >
                  Voltar ao Painel
                </Button>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>• O vídeo foi configurado como não listado (unlisted)</p>
                <p>• Incorporação permitida apenas no domínio do sistema</p>
                <p>• Downloads desabilitados para proteção do conteúdo</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
